// Where you are lives in the address, so a refresh, Back/Forward or a saved link lands
// in the same place:  /  /saved  /recent  /category/Bikes  /source/<id>  /topic/Hardline  /search?q=…
// plus /article/<id> when an article is open, ?show=all when read articles show, and
// &focus=1 while an open article is read in Focus (so a refresh stays in it), and
// settings=<section> while Settings is open (a refresh reopens it there; Back closes it).
import type { Filter, Scope } from "@vladrss/core";

export interface Place { scope: Scope; filter: Filter; article: string | null; focus?: boolean; settings?: string }

export function toPath({ scope, filter, article, focus, settings }: Place): string {
  let path = scope.type === "all" ? (filter === "saved" ? "/saved" : "/")
    : scope.type === "recent" ? "/recent"
    : scope.type === "category" ? `/category/${encodeURIComponent(scope.name)}`
    : scope.type === "feed" ? `/source/${scope.feedId}`
    : scope.type === "topic" ? `/topic/${encodeURIComponent(scope.name)}`
    : "/search";
  if (article) path = `${path === "/" ? "" : path}/article/${article}`;
  const q = new URLSearchParams();
  if (scope.type === "search") q.set("q", scope.query);
  if (filter === "all" && scope.type !== "recent") q.set("show", "all");
  if (focus && article) q.set("focus", "1");
  if (settings) q.set("settings", settings);
  const qs = q.toString();
  return path + (qs ? `?${qs}` : "");
}

export function fromLocation(loc: { pathname: string; search: string } = location): Place {
  // Addresses can be pasted or damaged outside the app. Reject a malformed escape
  // before decoding individual segments, including during module initialization.
  try { decodeURIComponent(loc.pathname); }
  catch { return { scope: { type: "all" }, filter: "unread", article: null }; }
  const q = new URLSearchParams(loc.search);
  let path = loc.pathname;
  let article: string | null = null;
  const m = /^(.*?)\/article\/([^/]+)\/?$/.exec(path);
  if (m) { path = m[1] || "/"; article = decodeURIComponent(m[2]!); }
  const parts = path.split("/").filter(Boolean);
  const showAll = q.get("show") === "all";
  let scope: Scope = { type: "all" };
  let filter: Filter = showAll ? "all" : "unread";
  if (parts[0] === "saved") filter = "saved";
  else if (parts[0] === "recent") { scope = { type: "recent" }; filter = "unread"; }
  else if (parts[0] === "category" && parts[1]) scope = { type: "category", name: decodeURIComponent(parts[1]) };
  else if (parts[0] === "source" && parts[1]) scope = { type: "feed", feedId: parts[1] };
  else if (parts[0] === "topic" && parts[1]) scope = { type: "topic", name: decodeURIComponent(parts[1]) };
  else if (parts[0] === "search" && q.get("q")) scope = { type: "search", query: q.get("q")! };
  const settings = q.get("settings");
  return { scope, filter, article, ...(article && q.get("focus") === "1" ? { focus: true } : {}), ...(settings ? { settings } : {}) };
}
